import React from 'react';
import RetentionTable from '../components/dashboard/RetentionTable';
import AppMetricsTable from '../components/dashboard/AppMetricsTable';
import RetentionTableSkeleton from '../components/dashboard/skeletons/RetentionTableSkeleton';
import AppMetricsTableSkeleton from '../components/dashboard/skeletons/AppMetricsTableSkeleton';
import { COUNTRIES } from '../components/dashboard/constants/countries';
import { DashboardHeader } from '../components/dashboard/DashboardHeader';
import { DashboardErrorBanner } from '../components/dashboard/DashboardErrorBanner';
import { DashboardStatCardsGrid } from '../components/dashboard/DashboardStatCardsGrid';
import { DashboardPrimaryGrid } from '../components/dashboard/DashboardPrimaryGrid';
import { DashboardChatAdsGrid } from '../components/dashboard/DashboardChatAdsGrid';
import { useDashboardData } from '../components/dashboard/hooks/useDashboardData';
import { useDashboardViewModel } from '../components/dashboard/hooks/useDashboardViewModel';
import { useDashboardChartVisibility } from '../components/dashboard/hooks/useDashboardChartVisibility';
import { useDashboardStatCardOrder } from '../components/dashboard/hooks/useDashboardStatCardOrder';
import { useDashboardSectionVisibility } from '../components/dashboard/hooks/useDashboardSectionVisibility';
import { DashboardBlockingOverlay } from '../components/dashboard/DashboardBlockingOverlay';
import { DashboardSectionFilter } from '../components/dashboard/DashboardSectionFilter';

const Dashboard: React.FC = () => {
  const sectionVisibility = useDashboardSectionVisibility();
  const data = useDashboardData(sectionVisibility.visibility);
  const chartVisibility = useDashboardChartVisibility();
  const statCardOrder = useDashboardStatCardOrder();

  const vm = useDashboardViewModel(
    data.tableCounts,
    data.overviewMetrics,
    data.appSessionMetrics,
    data.userMetrics,
    data.gameSessionMetrics,
    data.testSessionMetrics,
    data.chatSessionMetrics,
    data.advertisementMetrics
  );

  const overviewEmpty = data.overviewMetrics.length === 0;

  const blockingOverlayOpen = data.countryScopedLoading || data.dashboardBlockingLoading;
  const blockingSubtitle = data.dashboardBlockingLoading
    ? 'Tüm dashboard verileri yenileniyor…'
    : data.retentionScopedLoading && data.appMetricsScopedLoading
      ? 'Retention ve uygulama metrikleri güncelleniyor…'
      : data.retentionScopedLoading
      ? `${data.retentionCountries[0] ?? 'Tüm Ülkeler'} için retention verisi getiriliyor…`
      : `${data.retentionCountries[0] ?? 'Tüm Ülkeler'} için uygulama metrikleri getiriliyor…`;

  return (
    <div className="w-full max-w-7xl mx-auto space-y-6">
      <DashboardBlockingOverlay
        open={blockingOverlayOpen}
        subtitle={blockingSubtitle}
        progress={data.dashboardBlockingLoading ? data.dashboardRefreshProgress : null}
      />

      <DashboardHeader
        onRefresh={data.handleRefresh}
        disabled={data.isRefreshing || data.loading || blockingOverlayOpen}
        isRefreshing={data.isRefreshing}
        actionsSlot={
          <DashboardSectionFilter
            visibility={sectionVisibility.visibility}
            onToggle={sectionVisibility.toggle}
            onShowAll={sectionVisibility.showAll}
            onHideAll={sectionVisibility.hideAll}
          />
        }
      />

      {data.error && <DashboardErrorBanner message={data.error} />}

      {sectionVisibility.visibility.statCards && (
        <DashboardStatCardsGrid
          loading={data.loading}
          hasTableCounts={!!data.tableCounts}
          pageCards={vm.pageCards}
          order={statCardOrder.order}
          onReorder={statCardOrder.setOrder}
        />
      )}

      {sectionVisibility.visibility.primaryCharts && (
        <DashboardPrimaryGrid
          loading={data.loading}
          versionStatsLoading={!data.overviewReady}
          overviewMetricsEmpty={overviewEmpty}
          vm={vm}
          visibility={chartVisibility}
        />
      )}

      {sectionVisibility.visibility.chatAdsCharts && (
        <DashboardChatAdsGrid
          loading={data.loading}
          versionStatsLoading={!data.overviewReady}
          overviewMetricsEmpty={overviewEmpty}
          vm={vm}
          visibility={chartVisibility}
        />
      )}

      {sectionVisibility.visibility.retentionTable &&
        (data.loading && data.retentionData.length === 0 ? (
          <RetentionTableSkeleton />
        ) : (
          <RetentionTable
            retentionData={data.retentionData}
            retentionCountries={data.retentionCountries}
            countries={[...COUNTRIES]}
            loading={data.loading}
            actionsDisabled={blockingOverlayOpen}
            isRefreshing={data.retentionScopedLoading}
            onRetentionCountriesChange={data.setRetentionCountries}
            onRefresh={data.handleRefreshRetentionAndAppMetrics}
          />
        ))}

      {sectionVisibility.visibility.appMetricsTable &&
        (data.loading && data.appMetricsData.length === 0 ? (
          <AppMetricsTableSkeleton />
        ) : (
          <AppMetricsTable
            appMetricsData={data.appMetricsData}
            loading={data.loading}
            retentionCountries={data.retentionCountries}
          />
        ))}
    </div>
  );
};

export default Dashboard;
